"use client";

import { useEffect, useRef } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { TOP_MENU_BUTTON_OPEN_CLASS_NAME } from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { Icon } from "@/components/icons/Icon";
import { SearchInput } from "@/components/search/SearchInput";
import { SearchList } from "@/components/search/SearchList";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

const SearchPopoverInner = observer(function SearchPopoverInner({
  open,
  close,
}: {
  open: boolean;
  close: (el?: HTMLElement) => void;
}) {
  const { search } = useStore();
  const t = useTranslations();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      search.open();
    } else {
      search.close();
    }
  }, [open, search]);

  // Close the Popover when search is dismissed from inside (e.g. Escape in SearchInput,
  // clicking the clear button).
  useEffect(() => {
    if (!search.isOpen && open) {
      close();
    }
  }, [search.isOpen, open, close]);

  // Open the Popover when search.isOpen is triggered externally (e.g. keyboard shortcut).
  useEffect(() => {
    if (search.isOpen && !open) {
      buttonRef.current?.click();
    }
  }, [search.isOpen, open]);

  return (
    <>
      <PopoverButton
        ref={buttonRef}
        as={Sidebar.Button}
        icon={
          <Icon
            name="Search"
            className={twMerge("w-[28px] h-[28px]", open && "[&>path]:stroke-blue-500")}
          />
        }
        title={t("search.button")}
        hotkey="search"
        className={twMerge(open && TOP_MENU_BUTTON_OPEN_CLASS_NAME)}
      />
      <PopoverPanel
        portal
        anchor={{ to: "right start", gap: 14, padding: 10, offset: -5 }}
        className={twMerge(
          "z-100 flex min-h-0 flex-col overflow-hidden rounded-12",
          "-m-1 p-1",
          "outline-none",
          "w-sidebar-width min-w-sidebar-width max-h-[min(70vh,calc(100vh-3rem))]",
        )}
      >
        <SearchInput />
        <SearchList />
      </PopoverPanel>
    </>
  );
});

export const SearchPopover = observer(function SearchPopover() {
  return (
    <Popover className="relative flex">
      {({ open, close }) => <SearchPopoverInner open={open} close={close} />}
    </Popover>
  );
});
